import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import moment from 'moment';
import cx from 'classnames';
import FlexBox from 'flexbox-react';
import { Input, Checkbox, Switch } from 'react-toolbox';
import DatePicker from 'components/DatePicker';
import BreakLine from 'components/BreakLine';
import PhoneInput from 'components/PhoneType/PhoneInput';
import StyledSelect from 'components/StyledSelect';
import StoreSelect from 'components/formElements/StoreSelect';
import SurfaceTypeSelect from 'components/formElements/SurfaceTypeSelect';
import styles from 'styles/form';
import { STATES, STATES_WITH_ABBREV,
         MODE_OF_CONTACT, AUTO_COMM_METHOD } from 'config/constants';
import { error, vendorDisplayName } from '../../../../../../utils';
import RTCheckbox from 'styles/checkbox.scss';

const STATES_SOURCE = STATES.map(state => ({ value: state, label: state }));
const largeInput = cx(styles.inputs, styles.largeInput);
const mediumInput = cx(styles.inputs, styles.mediumInput);
const smallInput = cx(styles.inputs, styles.smallInput);
const titleStyle = cx(styles.bottomTitle, styles.childTitle);
const determineStateValue = phaseState => (STATES.includes(phaseState) ?
  phaseState : STATES_WITH_ABBREV[phaseState]);

class PhaseBasicInfoForm extends Component {
  constructor(props) {
    super(props);
    this.handleDateSold = this.props.onChange.bind(this, 'date_sold');
    this.handleCustomerName = this.props.onChange.bind(this, 'customer_name');
    this.handleAssociate = this.props.onChange.bind(this, 'associate');
    this.handleStreetAddress = this.props.onChange.bind(this, 'street_address');
    this.handleCity = this.props.onChange.bind(this, 'city');
    this.handleState = this.props.onChange.bind(this, 'state');
    this.handleZipCode = this.props.onChange.bind(this, 'zip_code');
    this.handleYearBuilt = this.props.onChange.bind(this, 'year_built');
    this.handleEmail = this.props.onChange.bind(this, 'email');
    this.handleProjectNumber = this.props.onChange.bind(this, 'project_number');
    this.handleProductLocation = this.props.onChange.bind(this, 'product_location');
    this.handleVendorEta = this.props.onChange.bind(this, 'vendor_eta');
    this.handleRelatedPo = this.props.onChange.bind(this, 'related_po');
    this.handleSpigDelivery = this.props.onChange.bind(this, 'spig_delivery');
  }

  handleStore = (option) => {
    const selected = option && !Array.isArray(option) ? option : {};
    this.props.onChange('store', selected.value, selected.vendor);
  };
  handlePhoneNumber = index => this.props.onChange.bind(this, 'phone_numbers', index, 'number');
  handleOkToText = index => this.props.onChange.bind(this, 'phone_numbers', index, 'ok_to_text');
  handleSurfaceType = option => this.props.onChange('surface_type', option.value, option.label);
  canEditYear = (role, source) => (source !== 'IFI' ? true : (role === 'admin' || role === 'regional'));

  render = () => {
    const { phase, onChange, invalidEmail, role, errors, phase: {
      phone_numbers = [], source = '', workroom: { name: workroom = '' } = {} } } = this.props;
    const stateVal = determineStateValue(phase.state);
    const surfaceId = phase.surface_type ? phase.surface_type.id : '';
    const editYear = this.canEditYear(role, source);
    const surfaceTypeDisabled = !phase.store.id;
    const surfaceTypePlaceholder = surfaceTypeDisabled
      ? 'Select a store first to enable Surface Type'
      : 'Select Surface Type';
    return (<div>
      <FlexBox flexWrap="wrap" className={styles.divider}>
        <h5 className={titleStyle}>Basic Information</h5>
        <BreakLine />
        <DatePicker
          label="Date sold/created"
          className={largeInput}
          value={phase.date_sold}
          maxDate={new Date()}
          onChange={this.handleDateSold}
          error={error('date_sold', errors)}
          required
        />
        <Input
          label="Customer Name"
          className={largeInput}
          value={phase.customer_name}
          onChange={this.handleCustomerName}
          error={error('customer_name', errors)}
          required
        />
        <Input
          label={`${source && source !== 'Manual' ? vendorDisplayName(source, workroom) : 'Vendor'} Associate`}
          className={largeInput}
          value={phase.associate || ''}
          onChange={this.handleAssociate}
          error={error('associate', errors)}
        />
        <BreakLine />
        <Input
          label="Street Address"
          className={largeInput}
          value={phase.street_address}
          onChange={this.handleStreetAddress}
          error={error('street_address', errors)}
          required
        />
        <Input
          label="City"
          className={largeInput}
          value={phase.city}
          onChange={this.handleCity}
          error={error('city', errors)}
          required
        />
        <StyledSelect
          className={largeInput}
          label="State"
          onChange={this.handleState}
          options={STATES_SOURCE}
          value={stateVal}
          placeholder={'Select State'}
          error={error('state', errors)}
          required
        />
        <Input
          label="Zip Code"
          className={smallInput}
          value={phase.zip_code}
          onChange={this.handleZipCode}
          error={error('zip_code', errors)}
          required
        />
        <BreakLine />
        <Input
          label="Year Built"
          className={smallInput}
          value={phase.year_built || ''}
          onChange={this.handleYearBuilt}
          disabled={!editYear}
          error={error('year_built', errors)}
        />
        <BreakLine />
        <h5 className={titleStyle}>Contact Information</h5>
        <BreakLine />
        {phone_numbers.map((phone, i) =>
          <PhoneInput
            label={`Phone Number ${i + 1}`}
            key={`phone${i}`} // eslint-disable-line react/no-array-index-key
            type="tel"
            value={phone.number}
            className={largeInput}
            onChange={this.handlePhoneNumber(i)}
            error={error(`phone_numbers[${i}].number`, errors)}
            required={i === 0}
          />,
        )}
        <Input
          label="Email"
          type="email"
          value={phase.email || ''}
          className={largeInput}
          onChange={this.handleEmail}
          error={invalidEmail ? 'Input valid email' : error('email', errors)}
        />
        <BreakLine />
        <div>
          <p className={styles.inputTitle}> OK to Text? </p>
          {phone_numbers[0].number && <Checkbox
            checked={phone_numbers[0].ok_to_text}
            label="Phone Number 1"
            theme={RTCheckbox}
            className={styles.checkboxListItem}
            onChange={value => onChange('phone_numbers', 0, 'ok_to_text', value)}
          />}
          {phone_numbers[1].number && <Checkbox
            checked={phone_numbers[1].ok_to_text}
            label="Phone Number 2"
            theme={RTCheckbox}
            className={styles.checkboxListItem}
            onChange={value => onChange('phone_numbers', 1, 'ok_to_text', value)}
          />}
          {phone_numbers[2].number && <Checkbox
            checked={phone_numbers[2].ok_to_text}
            label="Phone Number 3"
            theme={RTCheckbox}
            className={styles.checkboxListItem}
            onChange={value => onChange('phone_numbers', 2, 'ok_to_text', value)}
          />}
        </div>
        <BreakLine />
        <div>
          <p className={styles.inputTitle}> Primary Mode of Contact </p>
          {MODE_OF_CONTACT.map(contact => (<Checkbox
            key={`contact-${contact}`}
            checked={phase.mode_of_contact === contact}
            label={contact}
            theme={RTCheckbox}
            className={styles.checkboxListItem}
            onChange={value => onChange('mode_of_contact', contact, value)}
          />))}
        </div>
        <BreakLine />
        <div>
          <p className={styles.inputTitle}> Automatic Communication Method </p>
          {AUTO_COMM_METHOD.map(method => (<Checkbox
            key={`method-${method}`}
            checked={(phase.communication_method || '') === method.toLowerCase()}
            label={method}
            theme={RTCheckbox}
            className={styles.checkboxListItem}
            onChange={value => onChange('communication_method', method.toLowerCase(), value)}
          />))}
        </div>
        <BreakLine />
        <h5 className={titleStyle}>Purchase Information</h5>
        <BreakLine />
        <Input
          label="Project number"
          value={phase.project_number}
          className={largeInput}
          onChange={this.handleProjectNumber}
          error={error('project_number', errors)}
          required={phase.type !== 'Workorder'}
        />
        <StoreSelect
          multi={false}
          className={largeInput}
          onChange={this.handleStore}
          value={phase.store.id}
          label="Store"
          placeholder="Select Store Number"
          error={error('store', errors)}
          required
        />
        {phase.type !== 'Assessment' && <Fragment>
          <Input
            label="Product location"
            value={phase.product_location || ''}
            className={mediumInput}
            onChange={this.handleProductLocation}
            error={error('product_location', errors)}
          />
          <DatePicker
            label="Vendor ETA"
            className={mediumInput}
            value={phase.vendor_eta}
            minDate={moment().add(1, 'day').toDate()}
            onChange={this.handleVendorEta}
            error={error('vendor_eta', errors)}
          />
          <BreakLine />
          <Switch
            className={styles.specificSwitch}
            name="spigDelivery"
            label="SPIG Delivery"
            onChange={this.handleSpigDelivery}
            checked={phase.spig_delivery}
            disabled={false}
          />
        </Fragment>}
        {phase.type === 'Workorder' && <Input
          label="Related Purchase Order"
          value={phase.related_po || ''}
          className={largeInput}
          onChange={this.handleRelatedPo}
          error={error('related_po', errors)}
        />}
        <BreakLine />
        <h5 className={titleStyle}>Surface Information</h5>
        <BreakLine />
        <SurfaceTypeSelect
          placeholder={surfaceTypePlaceholder}
          className={largeInput}
          multi={false}
          onChange={this.handleSurfaceType}
          value={surfaceId}
          vendor={phase.store.vendor}
          disabled={surfaceTypeDisabled}
          error={error('surface_type', errors)}
          required
        />
      </FlexBox>
    </div>);
  }
}

PhaseBasicInfoForm.propTypes = {
  onChange: PropTypes.func,
  errors: PropTypes.object,
  phase: PropTypes.object,
  role: PropTypes.string.isRequired,
  invalidEmail: PropTypes.bool,
};

export default PhaseBasicInfoForm;



// WEBPACK FOOTER //
// ./client/app/screens/main/screens/phases/phaseEdit/components/phaseBasicInfoForm.jsx